import { Typography } from "@mui/material";
import { Fragment, memo, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Icon } from "../../../../common/components";
import { MaterialSymbolType } from "../../../../common/components/Icon/IconConstant";
import { createStyles, palette, spacing } from "../../../../styles/theme";
import {
  ColorBleedingItem,
  ColorEndOfLifeItem,
  DeprecatedDesignItem,
  ProductDefinitionPartElementDisplay,
} from "../../../../types";
import {
  getConditionnalStyle,
  useThemeBreakpoints,
} from "../../../../utils/themeUtils";
import { DarkTooltip, Dot } from "../../../../common/components";

const getStyles = (nameContainer: HTMLDivElement | null, isDefault?: boolean) =>
  createStyles({
    container: {
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: { xs: spacing.xxsMobile, lg: spacing.smallDesktop },
      p: { xs: spacing.xsMobile, lg: spacing.smallDesktop },
      justifyContent: { xs: "center", lg: "inherit" },
      minHeight: { xs: 66, lg: 48 },
      maxHeight: { xs: 66, lg: 48 },
      minWidth: { xs: 56, lg: "100%" },
      maxWidth: { xs: 56, lg: undefined },
      borderRadius: "8px",
      border: "2px solid transparent",
      flexDirection: { xs: "column", lg: "row" },
      overflow: "hidden",
      "&:hover": {
        background: palette.blue.paleEffect,
      },
    },
    active: {
      border: `2px solid ${palette.blue.secondary}`,
    },
    disabled: {
      cursor: "default",
      "&:hover": {
        background: "transparent",
      },
    },
    imgContainer: {
      minWidth: { xs: 48, lg: 40 },
      display: "flex",
      alignItems: { xs: !isDefault ? "baseline" : "center", lg: "center" },
      justifyContent: "center",
      flex: { xs: "1", lg: undefined },
      ">img": {
        borderRadius: "4px",
        maxWidth: { xs: "calc(56px - 2px - 4px - 2px - 4px)", lg: 40 }, // (boxWidth - border - paddingLeft - paddingRight - border)
        minWidth: { xs: undefined, lg: 40 },
        width: { xs: "100%", lg: 40 },
        maxHeight: {
          xs: `min(32px, calc(66px - 2px - 4px - ${nameContainer?.clientHeight ?? 10}px - 2px - 4px - 2px))`, // (boxHeight - border - paddingTop - textHeight - gap  - paddingBottom - border)
          lg: 32,
        },
        minHeight: { xs: undefined, lg: 32 },
        height: { xs: "100%", lg: 32 },
        flex: { xs: "0 1 auto", lg: undefined },
        objectFit: "contain",
      },
    },
    textContainer: {
      flex: { xs: "1 1 auto", lg: undefined },
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      overflow: "hidden",
    },
  });

interface Item {
  onClick: (() => void) | undefined;
  productName?: string;
  name?: string;
  labelKey?: string;
  label?: string;
  isSelected: boolean;
  display: ProductDefinitionPartElementDisplay & { icon?: MaterialSymbolType };
  enabled?: boolean;
  deprecated?: boolean;
  reasonForBeingDisabled?: {
    msg: string;
    disabledByStyle?: string;
    disabledByPart?: string;
  };
  deprecatedDesignItems?: DeprecatedDesignItem[];
  colorBleedingItems?: ColorBleedingItem[];
  colorEndOfLifeItems?: ColorEndOfLifeItem[];
  canDelete?: boolean;
  canHide?: boolean;
  active?: boolean;
  onHide?: () => void;
  onShow?: () => void;
  onDelete?: () => void;
  isDefault?: boolean;
  inWarning?: boolean;
}

interface Props {
  item: Item;
  isDesignLocked?: boolean;
  isDecoration?: boolean;
}

const LeftSidebarItem = ({ item, isDesignLocked, isDecoration }: Props) => {
  const nameContainerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const { isMobile } = useThemeBreakpoints();
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const hasWarningIcon = useMemo(() => {
    if (item) {
      if (
        (item.deprecatedDesignItems && item.deprecatedDesignItems.length) ||
        (item.colorBleedingItems && item.colorBleedingItems.length !== 0) ||
        item.deprecated ||
        item.inWarning
      ) {
        return true;
      }
    }
    return false;
  }, [item]);

  const hasEndOfLifeIcon = useMemo(() => {
    if (item) {
      if (item.colorEndOfLifeItems && item.colorEndOfLifeItems.length !== 0) {
        return true;
      }
    }
    return false;
  }, [item]);

  const label = item.labelKey ? t(item.labelKey) : item.label;

  const styles = useMemo(
    () => getStyles(nameContainerRef.current, item.isDefault),
    // eslint-disable-next-line
    [nameContainerRef?.current, item.isDefault]
  );

  const MobileTooltip = isMobile && !item.enabled ? DarkTooltip : Fragment;
  const DesktopTooltip = isMobile && !item.enabled ? Fragment : DarkTooltip;
  const toolTipProps = {
    title: t("specs.element_not_available", {
      affectedElement: label,
      style:
        item.productName && item.reasonForBeingDisabled?.disabledByStyle
          ? t(
              `specs.${item.productName}.${item.reasonForBeingDisabled?.disabledByStyle}`
            )
          : undefined,
      part:
        item.productName && item.reasonForBeingDisabled?.disabledByPart
          ? t(
              `specs.${item.productName}.${item.reasonForBeingDisabled?.disabledByPart}`
            )
          : undefined,
    }),
    followCursor: !isMobile,
  };

  const getTextColor = () => {
    if (!isDecoration) {
      return palette.grey.nearBlack;
    }
    if (isDesignLocked || (!item.active && !item.isDefault)) {
      return palette.grey.darkerGrey;
    }
    if (!item.enabled) {
      return palette.grey.primary;
    }
    return palette.grey.nearBlack;
  };

  return (
    item?.display?.displayable && (
      <Box
        sx={[
          styles.container,
          getConditionnalStyle(item.isSelected, styles.active),
          getConditionnalStyle(!item.enabled, styles.disabled),
        ]}
        onClick={() => {
          item?.onClick?.();
        }}
      >
        <Box sx={styles.imgContainer}>
          {item.display.menuIconUrl ? (
            <img
              alt="iconUrl"
              src={item.display.menuIconUrl}
              style={{ backgroundColor: item.display.backgroundColor }}
            />
          ) : (
            item.display.icon && (
              <Icon
                icon={item.display.icon}
                size={24}
                color={isDesignLocked ? palette.grey.darkerGrey : undefined}
              />
            )
          )}
        </Box>
        <Box
          display={"flex"}
          alignItems={"center"}
          justifyContent={{ xs: "center", lg: "space-between" }}
          fullWidth
          // flex={{ xs: undefined, lg: undefined }}
        >
          <MobileTooltip
            {...((isMobile
              ? {
                  open: isTooltipOpen && !item.enabled,
                  onOpen: () => setIsTooltipOpen(true),
                  onClose: () => setIsTooltipOpen(false),
                  leaveDelay: 3000,
                  leaveTouchDelay: 3000,
                  ...toolTipProps,
                }
              : {}) as any)}
          >
            <Box
              ref={nameContainerRef}
              display={"flex"}
              alignItems={"center"}
              onClick={() => setIsTooltipOpen((prev) => !prev)}
            >
              <Typography
                variant={isMobile ? "textXxs" : "textRegular"}
                textAlign={isMobile ? "center" : "left"}
                color={getTextColor()}
              >
                {label}
              </Typography>
              {hasWarningIcon && <Dot color="warning" sx={{ ml: "5px" }} />}
              {hasEndOfLifeIcon && <Dot color="success" sx={{ ml: "5px" }} />}
              {!item.enabled && (
                <DesktopTooltip {...((!isMobile ? toolTipProps : {}) as any)}>
                  <Icon
                    icon="info"
                    size={16}
                    color={palette.grey.primary}
                    style={{ marginLeft: spacing.xsDesktop }}
                  />
                </DesktopTooltip>
              )}
            </Box>
          </MobileTooltip>
          {!isMobile && (
            <Box
              display={"flex"}
              alignItems={"center"}
              gap={spacing.smallDesktop}
              sx={{
                pointerEvents: isDesignLocked ? "none" : "auto",
              }}
            >
              {item.canDelete && !isDesignLocked && (
                <Icon
                  onClick={item.onDelete}
                  icon="delete"
                  size={22}
                  color={
                    isDesignLocked || (!item.active && !item.isDefault)
                      ? palette.grey.darkerGrey
                      : undefined
                  }
                />
              )}
              {item.canHide && !item.active && (
                <Icon
                  onClick={item.onShow}
                  icon="visibility_off"
                  size={22}
                  color={
                    isDesignLocked || (!item.active && !item.isDefault)
                      ? palette.grey.darkerGrey
                      : undefined
                  }
                />
              )}
              {item.canHide && item.active && (
                <Icon
                  onClick={item.onHide}
                  icon="visibility"
                  size={22}
                  color={isDesignLocked ? palette.grey.darkerGrey : undefined}
                />
              )}
            </Box>
          )}
        </Box>
      </Box>
    )
  );
};

export default memo(LeftSidebarItem);
